import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  PiCaretLeft,
  PiFilePdf,
  PiPencilSimple,
  PiPrinter,
  PiTrash,
} from 'react-icons/pi'
import {
  primaryPillButtonClass,
  secondaryPillButtonClass,
} from 'components/FbUI/secondaryPillButton'
import { cn } from 'components/UI/cn'
import { DashboardGrid } from './components/DashboardGrid'
import { DashboardTileCard } from './components/DashboardTile'
import { getDashboard } from './dashboards'
import { deleteSavedDashboard, getSavedDashboard } from './saved-dashboards'
import { REPORTING_PAGE_SHELL } from './page-shell'

/**
 * One dashboard: report tiles laid out on the grid. The layout is read-only here
 * — it renders each tile at its saved grid placement; rearranging (drag/resize,
 * snapping to the grid) happens in the builder via the Edit button. Tiles click
 * through to the full report detail page.
 */
function DashboardPageInner({ dashboardId }: { dashboardId: string }) {
  const navigate = useNavigate()
  // Shipped dashboards first, then fall back to user-built (saved) ones.
  const dashboard = getDashboard(dashboardId) ?? getSavedDashboard(dashboardId)
  // Only user-built dashboards can be deleted (shipped ones are defaults).
  const isSaved = !!getSavedDashboard(dashboardId)

  if (!dashboard) {
    return (
      <div className="p-8">
        <p className="text-fg-secondary">Dashboard not found.</p>
        <Link to="/reporting" className="text-brand">
          Back to reports
        </Link>
      </div>
    )
  }

  const handleDelete = () => {
    if (!dashboard) return
    if (!window.confirm(`Delete "${dashboard.name}"? This can't be undone.`))
      return
    deleteSavedDashboard(dashboardId)
    navigate('/reporting')
  }

  return (
    <div className={cn(REPORTING_PAGE_SHELL, 'gap-4')}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to="/reporting"
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg"
          >
            <PiCaretLeft size={14} /> Reports
          </Link>
          <h1 className="text-2xl font-bold text-fg">{dashboard.name}</h1>
          <p className="text-sm text-fg-secondary">{dashboard.description}</p>
        </div>
        {/* print:hidden keeps the toolbar out of the printout. Print is real
            (browser print → save as PDF); Export PDF is a placeholder until we
            add a PDF library. */}
        <div className="flex shrink-0 items-center gap-2 print:hidden">
          {isSaved && (
            <button
              type="button"
              onClick={handleDelete}
              className={cn(
                secondaryPillButtonClass,
                'hover:border-error hover:text-error'
              )}
            >
              <PiTrash size={16} /> Delete
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              navigate(`/reporting/dashboards/${dashboardId}/edit`)
            }
            className={secondaryPillButtonClass}
          >
            <PiPencilSimple size={16} /> Edit
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className={secondaryPillButtonClass}
          >
            <PiPrinter size={16} /> Print
          </button>
          <button
            type="button"
            onClick={() => {
              // Placeholder — real PDF export needs a library (jspdf/html-to-image).
              console.log('[reporting] Export to PDF (coming soon)')
            }}
            className={primaryPillButtonClass}
          >
            <PiFilePdf size={16} /> Export PDF
          </button>
        </div>
      </div>

      <DashboardGrid
        editable={false}
        items={dashboard.tiles.map((tile, i) => ({ id: String(i), tile }))}
        renderItem={({ tile }) => <DashboardTileCard tile={tile} />}
      />
    </div>
  )
}

export default function DashboardPage() {
  const { dashboardId = '' } = useParams()
  // Remount per dashboard so state initializes cleanly on navigation between
  // dashboards without a useEffect.
  return <DashboardPageInner key={dashboardId} dashboardId={dashboardId} />
}
